import React, { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PlantillaScreen from "../../helpers/PlantillaScreen";
import { LoaderScreen } from "../../components/LoaderScreen";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useBox } from "../../Finanzas/Helper/useBox";
// import FiltroReporteRefaccionesDetalleCliente from "./FiltroReporteRefaccionesDetalleCliente";
import { ChartComparativoAnual } from "./ChartComparativoAnual";
import { ChartVentaMensual } from "./ChartVentaMensual";
import { ChartFamiliasRefacciones } from "./ChartFamiliasRefacciones";
import { KpiCard } from "./KpiCard";
import { TablaKpisPorAnio } from "./TablaKpisPorAnio";
import { FormatMoney } from "../../../Helpers/FormatNumber";
import {
    filtrarDetallePorCliente,
    filtrarDetallePorFamilia,
    calcularKpisClienteDesdeDetalle,
    calcularKpisClientePorAnio,
    agruparVentaPorAnio,
    agruparVentasPorFamiliaYPeriodo,
    construirSeriesMensualesDesdeDetalle,
    obtenerOpcionesFamiliaDesdeDetalle,
} from "../../../Helpers/ReporteRefaccionesAgregaciones";

// Pantalla 3 (Detalle por Cliente / 360): se calcula sobre el `detalle`
// que reenvían la Pantalla 1 y la Pantalla 2 al hacer clic en un Cliente.
// Los KPIs del header son el ACUMULADO de `aniosMostrados` (a diferencia
// de Pantalla 2, que acota al año principal); la 4ª card los desglosa año
// por año (ver `kpisPorAnio`).

const filtroClienteVacio = () => ({ familia: "TODOS" });

// Formato corto dd/mm/aaaa para mostrar el rango de fechas heredado en el
// encabezado (las fechas llegan en formato ISO yyyy-mm-dd desde FechaInput).
const formatFechaCorta = (isoDate) => {
    if (!isoDate) return "";
    const [anio, mes, dia] = isoDate.split("-");
    return `${dia}/${mes}/${anio}`;
};

const ReporteRefaccionesDetalleCliente = () => {
    const history = useHistory();
    const { state } = useLocation();

    // Contexto heredado de las pantallas 1 y 2; valores por defecto si se entra directo por URL.
    const hoyIso = new Date().toISOString().split("T")[0];
    const boxstatePadre = state?.boxstatePadre || { fechaInicio: hoyIso, fechaFin: hoyIso };
    const familiaOrigen = state?.familia || { id: null, nombre: "Todas" };
    const cliente = state?.cliente || { id: null, nombre: "Cliente no especificado" };
    const detalleHeredado = state?.detalle || [];

    const [loader, setLoader] = useState({
        mostrar: false,
        alerta: false,
        error: false,
        mensaje: "",
    });

    const [boxstate, setBoxState] = useBox({
        familia: "TODOS",
    });

    // Filtro propio (solo Familia); se actualiza solo al presionar "Cargar".
    const [filtroClienteAplicado, setFiltroClienteAplicado] = useState(filtroClienteVacio);

    const onExecuteFiltroCliente = (nuevoFiltro) => {
        return new Promise((resolve) => {
            setFiltroClienteAplicado(nuevoFiltro);
            resolve(nuevoFiltro);
        });
    };

    // Año principal + comparativos (mismo cálculo que Pantalla 2).
    const anioPrincipal = boxstatePadre.fechaFin
        ? boxstatePadre.fechaFin.split("-")[0]
        : String(new Date().getFullYear());
    const aniosMostrados = useMemo(() => {
        const comparativos = (boxstatePadre.anio || []).map(String);
        const conjunto = new Set([...comparativos, anioPrincipal]);
        return [...conjunto].sort((a, b) => Number(a) - Number(b));
    }, [boxstatePadre.anio, anioPrincipal]);

    // Detalle acotado al Cliente, antes del filtro propio (opciones dinámicas + base para filtrar).
    const detalleCliente = useMemo(
        () => filtrarDetallePorCliente(detalleHeredado, cliente.id),
        [detalleHeredado, cliente.id]
    );

    const opcionesFamiliaCliente = useMemo(
        () => obtenerOpcionesFamiliaDesdeDetalle(detalleCliente),
        [detalleCliente]
    );

    // Detalle final: Cliente + filtro propio ya aplicado.
    const detalleClienteFiltrado = useMemo(() => {
        if (!filtroClienteAplicado.familia || filtroClienteAplicado.familia === "TODOS") {
            return detalleCliente;
        }
        return filtrarDetallePorFamilia(detalleCliente, filtroClienteAplicado.familia);
    }, [detalleCliente, filtroClienteAplicado]);

    // KPIs del header: acumulado de todos los años (sin `anioPrincipal`, no filtra por ejercicio).
    const kpisCalculados = useMemo(
        () => calcularKpisClienteDesdeDetalle(detalleClienteFiltrado),
        [detalleClienteFiltrado]
    );

    // Las 3 gráficas usan todos los años de `aniosMostrados`.
    const datosComparativoAnual = useMemo(
        () => agruparVentaPorAnio(detalleClienteFiltrado, aniosMostrados),
        [detalleClienteFiltrado, aniosMostrados]
    );

    const seriesMensuales = useMemo(
        () => construirSeriesMensualesDesdeDetalle(detalleClienteFiltrado, aniosMostrados),
        [detalleClienteFiltrado, aniosMostrados]
    );

    const datosMezclaFamilia = useMemo(
        () => agruparVentasPorFamiliaYPeriodo(detalleClienteFiltrado),
        [detalleClienteFiltrado]
    );

    // 4ª card: los 5 KPIs por año; `TablaKpisPorAnio` agrega la fila "Consolidado".
    const kpisPorAnio = useMemo(
        () => calcularKpisClientePorAnio(detalleClienteFiltrado, aniosMostrados),
        [detalleClienteFiltrado, aniosMostrados]
    );

    // No usa history.goBack() (perdería el estado local de la Pantalla 1); navega reenviando filtros.
    const onRegresar = () => {
        history.push({
            pathname: "/PostVenta/ReporteRefacciones",
            state: { filtrosReporteRefacciones: state?.filtrosReporteRefacciones },
        });
    };

    return (
        <PlantillaScreen
            titulo={`DETALLE CLIENTE: ${cliente.nombre.toUpperCase()}`}
            className={"plantilla-detalle-cliente"}
            className_main={1}
        >
            <LoaderScreen loader={loader} />
            <div className="peb-actions">
                <div className="peb-actions-regresar" onClick={onRegresar}>
                    <ArrowBackIcon /> Regresar
                </div>

                {/* <div className="peb-actions-filtro">
                    <FiltroReporteRefaccionesDetalleCliente
                        boxState={boxstate}
                        onBoxChange={setBoxState}
                        onExecute={onExecuteFiltroCliente}
                        opciones={{ familia: opcionesFamiliaCliente }}
                    />
                </div> */}

                <div className="peb-actions-contexto">
                    {/* Familia origen: {familiaOrigen.nombre} · Del{" "} */}
                    {formatFechaCorta(boxstatePadre.fechaInicio)} al{" "}
                    {formatFechaCorta(boxstatePadre.fechaFin)}
                </div>
            </div>

            <div className="pdc-kpis">
                <KpiCard titulo="Venta total" valor={`$${FormatMoney(kpisCalculados.ventaTotal, 0)}`} />
                {/* Metodología en calcularKpisClienteDesdeDetalle. */}
                <KpiCard
                    titulo="Repetitividad"
                    valor={`${kpisCalculados.repetitividad.toFixed(2)}`}
                />
                <KpiCard
                    titulo="Factura promedio"
                    valor={`$${FormatMoney(kpisCalculados.facturaPromedio, 0)}`}
                />
                <KpiCard titulo="Meses activos" valor={kpisCalculados.mesesActivos} />
                {/* Margen: (importe - costo) / importe. */}
                <KpiCard titulo="Margen" valor={`${(kpisCalculados.margen * 100).toFixed(1)}%`} />
            </div>

            <div className="pdc-body">
                <div className="pdc-col">
                    <div className="pdc-panel">
                        <div className="pdc-panel-title">Comparativo anual</div>
                        <div className="pdc-panel-chart">
                            <ChartComparativoAnual datos={datosComparativoAnual} />
                        </div>
                    </div>
                    <div className="pdc-panel">
                        <div className="pdc-panel-title">
                            Venta mensual{aniosMostrados.length > 1 ? " (comparativo por año)" : ""}
                        </div>
                        <div className="pdc-panel-chart">
                            <ChartVentaMensual series={seriesMensuales} />
                        </div>
                    </div>
                </div>

                <div className="pdc-col">
                    <div className="pdc-panel">
                        <div className="pdc-panel-title">Mezcla por Familia</div>
                        <div className="pdc-panel-chart">
                            <ChartFamiliasRefacciones
                                datos={datosMezclaFamilia}
                                anios={aniosMostrados}
                                anioPrincipal={anioPrincipal}
                            />
                        </div>
                    </div>

                    <div className="pdc-panel">
                        <div className="pdc-panel-title">KPIs por año</div>
                        <TablaKpisPorAnio
                            filas={kpisPorAnio}
                            anioPrincipal={anioPrincipal}
                            consolidado={kpisCalculados}
                        />
                    </div>
                </div>
            </div>
        </PlantillaScreen>
    );
};

export default ReporteRefaccionesDetalleCliente;
